import React, { useEffect, useRef, useState, useCallback, useMemo, useReducer } from "react";
import * as THREE from "three";
import { useSwipeable } from "react-swipeable";
import { VideoData, EasingFunctionType } from "./types";
import { animateFrames, getFrameName, getFrameNumber, Easing } from "./animation-utils";
import styled from "styled-components";
import { useImagePreloader, isVideoNavigable } from "./useImagePreloader";
import { TextureManager } from "./textureManager";
import TimelineNavigation from "./TimelineNavigation";

const ViewerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #08090a;
  border-radius: 4px;
  overflow: hidden;
`;

const SwipeableWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  z-index: 20;
`;

const LoadingText = styled.div`
  color: #b7bac1;
  text-align: center;
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-family: Inter;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 23px;
  margin-bottom: 8px;
`;

const ProgressBar = styled.div`
  width: 80%;
  max-width: 400px;
  height: 4px;
  border-radius: 8px;
  border: 0.5px solid rgba(62, 62, 68, 0.6);
  background: rgba(40, 40, 44, 0.6);
  backdrop-filter: blur(2px);
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${(props) => props.progress}%;
  background: #f7f8f8;
  transition: width 0.3s ease;
`;

interface ThreeJSFrameViewerProps {
  jsonPath: string;
  framesPath: string;
  transitionDuration?: number;
  easingFunction?: EasingFunctionType;
  adaptiveSpeed?: boolean;
  maxDuration?: number;
  frameSkipRate?: number;
  onVideoChanged?: (index: number, totalVideos: number) => void;
  onTransitionChange?: (isTransitioning: boolean) => void;
  navigatorRef?: React.RefObject<{
    nextVideo: () => void;
    prevVideo: () => void;
    goToVideo: (index: number) => void;
  } | null>;
  hideTimelineNavigation?: boolean;
  renderExternalNavigation?: any;
}

// State reducer for more organized state management
type State = {
  videoData: VideoData | null;
  currentVideoIndex: number;
  previousVideoIndex: number;
  targetVideoIndex: number;
  currentFrame: string;
  isTransitioning: boolean;
  transitionDurationMs: number;
  showVideoList: boolean;
};

type Action =
  | { type: "SET_VIDEO_DATA"; payload: VideoData }
  | { type: "SET_CURRENT_VIDEO_INDEX"; payload: number }
  | { type: "SET_CURRENT_FRAME"; payload: string }
  | { type: "SET_TRANSITIONING"; payload: boolean }
  | { type: "START_TRANSITION"; payload: { previousIndex: number; targetIndex: number; durationMs: number } }
  | { type: "COMPLETE_TRANSITION"; payload: number }
  | { type: "TOGGLE_VIDEO_LIST" };

const initialState: State = {
  videoData: null,
  currentVideoIndex: 0,
  previousVideoIndex: 0,
  targetVideoIndex: 0,
  currentFrame: "",
  isTransitioning: false,
  transitionDurationMs: 800,
  showVideoList: false,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_VIDEO_DATA":
      return { ...state, videoData: action.payload };
    case "SET_CURRENT_VIDEO_INDEX":
      return { ...state, currentVideoIndex: action.payload };
    case "SET_CURRENT_FRAME":
      return { ...state, currentFrame: action.payload };
    case "SET_TRANSITIONING":
      return { ...state, isTransitioning: action.payload };
    case "START_TRANSITION":
      return {
        ...state,
        previousVideoIndex: action.payload.previousIndex,
        targetVideoIndex: action.payload.targetIndex,
        transitionDurationMs: action.payload.durationMs,
        isTransitioning: true,
      };
    case "COMPLETE_TRANSITION":
      return {
        ...state,
        currentVideoIndex: action.payload,
        previousVideoIndex: action.payload,
        targetVideoIndex: action.payload,
        transitionDurationMs: 800,
        isTransitioning: false,
      };
    case "TOGGLE_VIDEO_LIST":
      return { ...state, showVideoList: !state.showVideoList };
    default:
      return state;
  }
}

const ThreeJSFrameViewer: React.FC<ThreeJSFrameViewerProps> = ({
  jsonPath,
  framesPath,
  transitionDuration = 800,
  easingFunction = "balancedSpring",
  adaptiveSpeed = true,
  maxDuration = 2500,
  frameSkipRate = 1,
  onVideoChanged,
  onTransitionChange,
  navigatorRef,
  hideTimelineNavigation = false,
  renderExternalNavigation,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [videoListData, setVideoListData] = useState<VideoData | null>(null);
  const {
    videoData,
    currentVideoIndex,
    previousVideoIndex,
    targetVideoIndex,
    currentFrame,
    isTransitioning,
    transitionDurationMs,
    showVideoList,
  } = state;

  // Use the image preloader hook
  const preloadStatus = useImagePreloader(videoData, framesPath);

  // ThreeJS related refs
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const planeRef = useRef<THREE.Mesh | null>(null);
  const textureRef = useRef<THREE.Texture | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const cancelAnimationRef = useRef<(() => void) | null>(null);
  const textureManagerRef = useRef<TextureManager | null>(null);
  const needsRenderRef = useRef<boolean>(false);

  // Map easing function name to actual function
  const getEasingFunction = useMemo(() => {
    return Easing[easingFunction as keyof typeof Easing] || Easing.balancedSpring;
  }, [easingFunction]);

  // Calculate transition duration using the same logic as animateFrames
  const calculateTransitionDuration = useCallback(
    (
      startFrameName: string,
      endFrameName: string,
      direction: "forward" | "backward",
      totalFrames: number,
      skipRate: number,
      dynamicMaxDuration: number,
    ): number => {
      const startFrame = getFrameNumber(startFrameName);
      const endFrame = getFrameNumber(endFrameName);

      // Calculate total frames to animate (same logic as animateFrames)
      let totalFramesToAnimate: number;

      if (direction === "forward") {
        if (endFrame > startFrame) {
          totalFramesToAnimate = Math.ceil((endFrame - startFrame) / skipRate);
        } else {
          totalFramesToAnimate = Math.ceil((totalFrames - startFrame + endFrame) / skipRate);
        }
      } else {
        if (startFrame > endFrame) {
          totalFramesToAnimate = Math.ceil((startFrame - endFrame) / skipRate);
        } else {
          totalFramesToAnimate = Math.ceil((startFrame + (totalFrames - endFrame)) / skipRate);
        }
      }

      if (totalFramesToAnimate === 0) {
        return transitionDuration;
      }

      // Calculate optimal duration (same logic as animateFrames)
      const baseFps = 24;
      const idealDuration = totalFramesToAnimate * (1000 / baseFps);
      const minDuration = 300;

      return adaptiveSpeed ? Math.min(Math.max(idealDuration, minDuration), dynamicMaxDuration) : transitionDuration;
    },
    [adaptiveSpeed, transitionDuration],
  );

  // Generate frame name from number
  const generateFrameName = useCallback(
    (frameNumber: number): string => {
      if (!videoData) return "";
      const { framePrefix, frameDigits, frameExtension } = videoData.metadata;
      return getFrameName(frameNumber, framePrefix, frameDigits, frameExtension);
    },
    [videoData],
  );

  // Render function that only renders when needed
  const renderScene = useCallback(() => {
    if (rendererRef.current && sceneRef.current && cameraRef.current && needsRenderRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
      needsRenderRef.current = false;
    }
  }, []);

  // Request render when needed
  const requestRender = useCallback(() => {
    if (!needsRenderRef.current) {
      needsRenderRef.current = true;
      requestAnimationFrame(renderScene);
    }
  }, [renderScene]);

  // Initialize ThreeJS scene
  const initThreeJS = useCallback(() => {
    if (!containerRef.current) return;

    // Initialize texture manager
    textureManagerRef.current = new TextureManager(framesPath, {
      maxCacheSize: 50,
      onTextureLoad: () => requestRender(),
      onTextureError: (frameName, error) => {
        console.error(`Error loading texture: ${frameName}`, error);
      },
    });

    // Create scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x08090a);
    sceneRef.current = scene;

    // Create camera
    const aspectRatio = 16 / 9;
    const camera = new THREE.PerspectiveCamera(50, aspectRatio, 0.1, 1000);
    camera.position.z = 1;
    cameraRef.current = camera;

    // Create renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance",
      alpha: false,
      depth: false,
      stencil: false,
      failIfMajorPerformanceCaveat: true,
    });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x08090a, 1.0);
    rendererRef.current = renderer;

    // Create a plane to display the texture
    const geometry = new THREE.PlaneGeometry(1.8, 1);
    const material = new THREE.MeshBasicMaterial({
      color: 0x08090a,
      side: THREE.DoubleSide,
    });
    const plane = new THREE.Mesh(geometry, material);
    scene.add(plane);
    planeRef.current = plane;

    // Add renderer to DOM
    containerRef.current.appendChild(renderer.domElement);

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current || !rendererRef.current || !cameraRef.current) return;

      const width = containerRef.current.clientWidth;
      const height = width / aspectRatio;

      rendererRef.current.setSize(width, height);
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
      requestRender();
    };

    // Initial sizing
    handleResize();
    window.addEventListener("resize", handleResize);

    // Cleanup function
    return () => {
      window.removeEventListener("resize", handleResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (rendererRef.current && containerRef.current) {
        containerRef.current.removeChild(rendererRef.current.domElement);
        rendererRef.current.dispose();
      }
      if (planeRef.current) {
        planeRef.current.geometry.dispose();
        if (planeRef.current.material instanceof THREE.Material) {
          planeRef.current.material.dispose();
        } else if (Array.isArray(planeRef.current.material)) {
          planeRef.current.material.forEach((material) => material.dispose());
        }
      }

      // Dispose of texture manager
      textureManagerRef.current?.dispose();
    };
  }, [requestRender, framesPath]);

  // Update texture with current frame
  const updateTexture = useCallback(
    async (frameName: string) => {
      if (!planeRef.current || !frameName || !textureManagerRef.current) return;

      try {
        const texture = await textureManagerRef.current.getTexture(frameName);

        if (planeRef.current && planeRef.current.material instanceof THREE.MeshBasicMaterial) {
          planeRef.current.material.map = texture;
          planeRef.current.material.color.setHex(0xffffff);
          planeRef.current.material.needsUpdate = true;
          requestRender();
        }
      } catch (error) {
        console.error(`Error loading texture: ${frameName}`, error);
      }
    },
    [requestRender],
  );

  // Load the JSON data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(jsonPath);
        const data: VideoData = await response.json();
        setVideoListData(data);

        dispatch({ type: "SET_VIDEO_DATA", payload: data });

        if (data.videos && data.videos.length > 0) {
          dispatch({ type: "SET_CURRENT_FRAME", payload: data.videos[0].startFrame });
          dispatch({ type: "COMPLETE_TRANSITION", payload: 0 });

          // Notify parent about video data
          if (onVideoChanged) {
            onVideoChanged(0, data.videos.length);
          }
        }
      } catch (error) {
        console.error("Error loading video data:", error);
      }
    };

    fetchData();
  }, [jsonPath, onVideoChanged]);

  // Initialize ThreeJS when component mounts
  useEffect(() => {
    const cleanup = initThreeJS();
    return cleanup;
  }, [initThreeJS]);

  // Update texture when current frame changes (allow if first frame is loaded)
  useEffect(() => {
    if (currentFrame && preloadStatus.firstFrameLoaded) {
      updateTexture(currentFrame);
    }
  }, [currentFrame, updateTexture, preloadStatus.firstFrameLoaded]);

  // Navigate to a specific video
  const navigateToVideo = useCallback(
    (targetIndex: number) => {
      if (!videoData || isTransitioning || targetIndex === currentVideoIndex) return;

      // Check if target video is navigable (has at least some frames loaded)
      if (!isVideoNavigable(preloadStatus, targetIndex)) {
        return;
      }

      // Cancel existing animation
      if (cancelAnimationRef.current) {
        cancelAnimationRef.current();
        cancelAnimationRef.current = null;
      }

      const startFrame = currentFrame;
      const endFrame = videoData.videos[targetIndex].startFrame;
      const startFrameNum = getFrameNumber(startFrame);
      const endFrameNum = getFrameNumber(endFrame);
      const frameDistance = Math.abs(endFrameNum - startFrameNum);

      // Set dynamic max duration for large jumps
      const dynamicMaxDuration = frameDistance > 100 ? Math.min(maxDuration, 7000) : maxDuration;

      // Determine direction
      const direction = targetIndex > currentVideoIndex ? "forward" : "backward";

      // Calculate the actual transition duration
      const actualDurationMs = calculateTransitionDuration(
        startFrame,
        endFrame,
        direction,
        videoData.metadata.totalFrames,
        frameSkipRate,
        dynamicMaxDuration,
      );

      // Start transition with previous and target indices and calculated duration
      dispatch({
        type: "START_TRANSITION",
        payload: { previousIndex: currentVideoIndex, targetIndex, durationMs: actualDurationMs },
      });

      // Notify parent about transition start
      if (onTransitionChange) {
        onTransitionChange(true);
      }

      // Start animation
      cancelAnimationRef.current = animateFrames(
        startFrame,
        endFrame,
        direction,
        videoData.metadata.totalFrames,
        generateFrameName,
        {
          fps: 24,
          transitionDuration: actualDurationMs,
          easing: getEasingFunction,
          adaptiveSpeed,
          maxDuration: dynamicMaxDuration,
          frameSkipRate,
          onFrameChange: (frameName) => {
            dispatch({ type: "SET_CURRENT_FRAME", payload: frameName });
          },
          onComplete: () => {
            dispatch({ type: "SET_CURRENT_FRAME", payload: endFrame });
            dispatch({ type: "COMPLETE_TRANSITION", payload: targetIndex });

            // Notify parent about transition end and video change
            if (onTransitionChange) {
              onTransitionChange(false);
            }
            if (onVideoChanged && videoData) {
              onVideoChanged(targetIndex, videoData.videos.length);
            }

            cancelAnimationRef.current = null;
          },
        },
      );
    },
    [
      videoData,
      currentVideoIndex,
      currentFrame,
      isTransitioning,
      generateFrameName,
      getEasingFunction,
      adaptiveSpeed,
      maxDuration,
      frameSkipRate,
      onVideoChanged,
      onTransitionChange,
      preloadStatus,
      calculateTransitionDuration,
    ],
  );

  // Setup navigator ref for parent component
  useEffect(() => {
    if (navigatorRef && navigatorRef.current) {
      navigatorRef.current.nextVideo = () => {
        if (!videoData || !preloadStatus.firstFrameLoaded) return;
        const nextIndex = (currentVideoIndex + 1) % videoData.videos.length;
        navigateToVideo(nextIndex);
      };

      navigatorRef.current.prevVideo = () => {
        if (!videoData || !preloadStatus.firstFrameLoaded) return;
        const prevIndex = (currentVideoIndex - 1 + videoData.videos.length) % videoData.videos.length;
        navigateToVideo(prevIndex);
      };

      navigatorRef.current.goToVideo = (index: number) => {
        if (!videoData || !preloadStatus.firstFrameLoaded) return;
        if (index >= 0 && index < videoData.videos.length) {
          navigateToVideo(index);
        }
      };
    }
  }, [
    navigatorRef,
    videoData,
    currentVideoIndex,
    navigateToVideo,
    preloadStatus.firstFrameLoaded,
    preloadStatus.videoStates,
  ]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!preloadStatus.firstFrameLoaded) return;

      if (e.key === "ArrowRight") {
        if (navigatorRef && navigatorRef.current) {
          navigatorRef.current.nextVideo();
        }
      } else if (e.key === "ArrowLeft") {
        if (navigatorRef && navigatorRef.current) {
          navigatorRef.current.prevVideo();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [navigatorRef, preloadStatus.firstFrameLoaded]);

  // Stable navigation functions to prevent infinite re-renders
  const stableNavigateNext = useCallback(() => {
    if (navigatorRef && navigatorRef.current) {
      navigatorRef.current.nextVideo();
    }
  }, [navigatorRef]);

  const stableNavigatePrev = useCallback(() => {
    if (navigatorRef && navigatorRef.current) {
      navigatorRef.current.prevVideo();
    }
  }, [navigatorRef]);

  // Swipe gesture handlers
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (preloadStatus.firstFrameLoaded && !isTransitioning) {
        stableNavigateNext();
      }
    },
    onSwipedRight: () => {
      if (preloadStatus.firstFrameLoaded && !isTransitioning) {
        stableNavigatePrev();
      }
    },
    trackMouse: false,
    trackTouch: true,
    preventScrollOnSwipe: true,
    delta: 60,
  });

  // Create navigation props for external rendering - memoized to prevent infinite loops
  const navigationProps = useMemo(() => {
    if (!videoData || !preloadStatus.firstFrameLoaded) return null;

    return {
      videos: videoData.videos,
      currentVideoIndex,
      previousVideoIndex,
      targetVideoIndex,
      transitionDurationMs,
      framesPath,
      onVideoSelect: navigateToVideo,
      isTransitioning,
      disabled: isTransitioning,
      preloadStatus,
      navigateNext: stableNavigateNext,
      navigatePrev: stableNavigatePrev,
    };
  }, [
    videoData,
    preloadStatus.firstFrameLoaded,
    currentVideoIndex,
    previousVideoIndex,
    targetVideoIndex,
    transitionDurationMs,
    framesPath,
    navigateToVideo,
    isTransitioning,
    preloadStatus,
    stableNavigateNext,
    stableNavigatePrev,
  ]);

  // Call external render function if provided - use useEffect to prevent infinite loops
  useEffect(() => {
    if (renderExternalNavigation && navigationProps) {
      renderExternalNavigation(navigationProps);
    }
  }, [renderExternalNavigation, navigationProps]);

  return (
    <SwipeableWrapper {...swipeHandlers}>
      <ViewerContainer ref={containerRef}>
        {!preloadStatus.firstFrameLoaded && (
          <LoadingOverlay>
            <LoadingText>Loading...</LoadingText>
            <ProgressBar>
              <ProgressFill progress={preloadStatus.globalProgress} />
            </ProgressBar>
          </LoadingOverlay>
        )}

        {!hideTimelineNavigation && navigationProps && <TimelineNavigation {...navigationProps} />}
      </ViewerContainer>
    </SwipeableWrapper>
  );
};

export default ThreeJSFrameViewer;

// Export the navigation props type for external use
export type NavigationProps = {
  videos: any[];
  currentVideoIndex: number;
  previousVideoIndex: number;
  targetVideoIndex: number;
  transitionDurationMs: number;
  framesPath: string;
  onVideoSelect: (index: number) => void;
  isTransitioning: boolean;
  disabled: boolean;
  preloadStatus: any;
  navigateNext: () => void;
  navigatePrev: () => void;
};
